import React from 'react';
import { Form, FormInstance, Input } from 'antd';
import {
  Banner,
  Gutter,
  Placeholder,
  TextAreaEmojiPicker,
} from '@common/components';
import { useLazyQuery } from '@common/hooks';
import { SUPPLIER_BY_EMAIL_DOMAIN_QUERY } from '@institutions/graphql';

export type NewSupplierFormValues = {
  supplierName: string;
  mainContactEmail: string;
  invitationMessage?: string;
};

export function SuppliersManagementNewSupplierForm({
  form,
  loading,
  onFormFinish,
}: {
  loading?: boolean;
  onFormFinish: (values: NewSupplierFormValues) => void;
  form: FormInstance<NewSupplierFormValues>;
}) {
  const [checkSupplierByEmail] = useLazyQuery(SUPPLIER_BY_EMAIL_DOMAIN_QUERY);

  return (
    <div className="suppliers-management-new-supplier-form">
      <Placeholder loading={loading}>
        <Form layout="vertical" form={form} onFinish={onFormFinish}>
          <Form.Item
            label="Supplier Name"
            name="supplierName"
            rules={[
              {
                required: true,
                message: 'Please enter the supplier name',
              },
            ]}
          >
            <Input size="large" placeholder="e.g., Awesome Staffing Inc." />
          </Form.Item>

          <Form.Item
            label="Main Contact Email"
            name="mainContactEmail"
            validateDebounce={500}
            rules={[
              {
                required: true,
                message: 'Please enter the main contact email',
              },
              {
                type: 'email',
                message: 'Please enter a valid email address',
              },
              {
                validator: async (_, value) => {
                  const { data } = await checkSupplierByEmail({
                    variables: { email: value },
                  });

                  if (!data?.supplierByEmailDomain) {
                    return;
                  }

                  const existingSupplier = data.supplierByEmailDomain;
                  const name =
                    existingSupplier.type === 'Supplier'
                      ? existingSupplier.name
                      : `${existingSupplier.name} (pending signup)`;

                  throw new Error(
                    `A supplier with this email domain already exists: ${name}`
                  );
                },
              },
            ]}
          >
            <Input size="large" placeholder="Type email" />
          </Form.Item>

          <Gutter direction="bottom" size="md">
            <Banner showIcon type="info">
              This email will receive an invitation. Upon acceptance, this user
              will become the default for the account.
            </Banner>
          </Gutter>

          <Form.Item label="Invitation Message" name="invitationMessage">
            <TextAreaEmojiPicker
              placeholder="Type a message to include with your invite"
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item>
        </Form>
      </Placeholder>
    </div>
  );
}
